// ========================================
// PINTEREST OAUTH CALLBACK HANDLER
// ========================================
import React, { useEffect, useState } from 'react';
import { pinterestOAuth } from '../../services/oauth/pinterestOAuth';
import { auth } from '../../firebase';

const PinterestCallback: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Pinterest koppeling aan het verwerken...');
  
  useEffect(() => {
    const executionKey = 'pinterest_callback_executing';
    const isExecuting = sessionStorage.getItem(executionKey);
    
    if (isExecuting === 'true') {
      console.log('⏭️ Pinterest callback already executing, skipping...');
      return;
    }
    
    console.log('📌 PinterestCallback component mounted');
    sessionStorage.setItem(executionKey, 'true');
    handleCallback();
    
    return () => {
      console.log('🧹 Component unmounting');
    };
  }, []);
  
  const handleCallback = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');
      const errorDescription = params.get('error_description');
      
      if (error) {
        setStatus('error');
        setMessage(errorDescription || 'OAuth autorisatie geweigerd');
        
        const returnUrl = sessionStorage.getItem('pinterestReturnUrl') || '#social-media';
        sessionStorage.removeItem('pinterestReturnUrl');
        
        setTimeout(() => {
          window.location.href = window.location.origin + returnUrl;
        }, 3000);
        return;
      }
      
      if (!code || !state) {
        setStatus('error');
        setMessage('Ongeldige callback parameters');
        
        const returnUrl = sessionStorage.getItem('pinterestReturnUrl') || '#social-media';
        sessionStorage.removeItem('pinterestReturnUrl');
        
        setTimeout(() => {
          window.location.href = window.location.origin + returnUrl;
        }, 3000);
        return;
      }
      
      console.log('🔐 Processing Pinterest OAuth callback...');
      
      // Wait for Firebase auth
      setMessage('Wachten op authenticatie...');
      
      const waitForAuth = new Promise<boolean>((resolve) => {
        let attempts = 0;
        const maxAttempts = 20;
        
        const checkAuth = () => {
          attempts++;
          if (auth.currentUser) {
            resolve(true);
          } else if (attempts >= maxAttempts) {
            resolve(false);
          } else {
            setTimeout(checkAuth, 500);
          }
        };
        
        checkAuth();
      });
      
      const isAuthenticated = await waitForAuth;
      
      if (!isAuthenticated) {
        setStatus('error');
        setMessage('Je moet ingelogd zijn om Pinterest te koppelen. Log eerst in en probeer opnieuw.');
        setTimeout(() => {
          window.location.href = window.location.origin;
        }, 4000);
        return;
      }
      
      setMessage('Pinterest token aan het ophalen...');
      
      const result = await pinterestOAuth.handleCallback(code, state);
      
      sessionStorage.removeItem('pinterest_callback_executing');
      
      if (result.success) {
        setStatus('success');
        setMessage('✅ Pinterest succesvol gekoppeld!');
        
        const returnUrl = sessionStorage.getItem('pinterestReturnUrl') || '#social-media';
        sessionStorage.removeItem('pinterestReturnUrl');
        
        setTimeout(() => {
          window.location.href = window.location.origin + returnUrl;
        }, 2000);
      } else {
        setStatus('error');
        setMessage(result.error || 'Koppeling mislukt');
        
        const returnUrl = sessionStorage.getItem('pinterestReturnUrl') || '#social-media';
        sessionStorage.removeItem('pinterestReturnUrl');
        
        setTimeout(() => {
          window.location.href = window.location.origin + returnUrl;
        }, 3000);
      }
      
    } catch (error) {
      console.error('❌ OAuth callback error:', error);
      
      sessionStorage.removeItem('pinterest_callback_executing');
      
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Er is een fout opgetreden');
      
      const returnUrl = sessionStorage.getItem('pinterestReturnUrl') || '#social-media';
      sessionStorage.removeItem('pinterestReturnUrl');
      
      setTimeout(() => {
        window.location.href = window.location.origin + returnUrl;
      }, 3000);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        {/* Status Icon */}
        <div className="text-center mb-6">
          {status === 'processing' && (
            <div className="inline-block">
              <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-red-600 mx-auto"></div>
              <div className="text-6xl mt-4">📌</div>
            </div>
          )}
          
          {status === 'success' && (
            <div className="animate-bounce">
              <div className="text-8xl">✅</div>
            </div>
          )}
          
          {status === 'error' && (
            <div className="animate-pulse">
              <div className="text-8xl">❌</div>
            </div>
          )}
        </div>
        
        {/* Status Message */}
        <h2 className={`text-2xl font-bold text-center mb-4 ${
          status === 'success' ? 'text-green-600' : 
          status === 'error' ? 'text-red-600' : 
          'text-gray-800'
        }`}>
          {status === 'processing' && 'Pinterest Koppelen...'}
          {status === 'success' && 'Gekoppeld!'}
          {status === 'error' && 'Oeps!'}
        </h2>
        
        <p className="text-center text-gray-600 mb-6">
          {message}
        </p>
        
        {/* Progress Steps */}
        {status === 'processing' && (
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold mr-3">
                ✓
              </div>
              <span>Autorisatie ontvangen</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center mr-3">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              </div>
              <span>Access token ophalen...</span>
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-xs mr-3">
                3
              </div>
              <span>Profiel ophalen</span>
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-xs mr-3">
                4
              </div>
              <span>Verbinding opslaan</span>
            </div>
          </div>
        )}
        
        {/* Success Message */}
        {status === 'success' && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
            <p className="text-green-800 font-semibold mb-2">
              🎉 Je Pinterest account is nu gekoppeld!
            </p>
            <p className="text-sm text-green-700">
              Je kunt nu automatisch pins maken naar Pinterest.
            </p>
          </div>
        )}
        
        {/* Error Message */}
        {status === 'error' && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-semibold mb-2 text-center">
              Er ging iets mis bij het koppelen
            </p>
            <p className="text-sm text-red-700 text-center mb-4">
              Probeer het opnieuw of neem contact op met support.
            </p>
            <button
              onClick={() => window.location.href = window.location.origin + '#dashboard'}
              className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Terug naar Dashboard
            </button>
          </div>
        )}
        
        {/* Pinterest Notice */}
        {status === 'processing' && (
          <div className="mt-6 p-3 bg-red-50 rounded-lg">
            <p className="text-xs text-red-700 text-center">
              💡 Pinterest vereist een afbeelding bij elke pin
            </p>
          </div>
        )}
        
        {/* Redirect Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Je wordt automatisch doorgestuurd...
          </p>
        </div>
      </div>
    </div>
  );
};

export default PinterestCallback;
